import {
	Dialog,
	DialogBackdrop,
	Transition,
	TransitionChild,
} from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/24/solid";
import React, { FC, Fragment, useState } from "react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonClose from "shared/ButtonClose/ButtonClose";
import services from "utils/services";

export interface ModalCongesRecupMultiProps {
	show: boolean;
	employees: any[];
	onCloseModal: () => void;
	onSaved: () => void;
}

const ModalCongesRecupMulti: FC<ModalCongesRecupMultiProps> = ({
	show,
	employees,
	onCloseModal,
	onSaved,
}) => {
	const [type, setType] = useState("");
	const [dateFrom, setDateFrom] = useState<any>("");
	const [dateTo, setDateTo] = useState<any>("");
	const [qty, setQty] = useState<any>(0);
	const [comment, setComment] = useState("");
	const [saving, setSaving] = useState(false);

	const onSave = async () => {
		setSaving(true);
		try {
			await new Promise<void>((resolve, reject) => {
				services.saveCongesRecupBulk(
					{ type, dateFrom, dateTo, qty, comment, employees },
					() => resolve(),
					(err: any) => reject(err)
				);
			});
			setType("");
			setQty(0);
			setDateFrom("");
			setDateTo("");
			setComment("");
			onSaved();
		} finally {
			setSaving(false);
		}
	};

	return (
		<Transition appear show={show} as={Fragment}>
			<Dialog as="div" className="fixed inset-0 z-50" onClose={onCloseModal}>
				<div className="flex items-stretch md:items-center justify-center h-full text-center md:px-4">
					<TransitionChild
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0"
						enterTo="opacity-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
					>
						<DialogBackdrop className="fixed inset-0 bg-black/40 dark:bg-black/70" />
					</TransitionChild>

					<span className="inline-block align-middle" aria-hidden="true">
						&#8203;
					</span>
					<TransitionChild
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0 scale-95"
						enterTo="opacity-100 scale-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100 scale-100"
						leaveTo="opacity-0 scale-95"
					>
						<div className="relative inline-flex xl:py-8 w-full max-h-full min-w-[50%] max-w-[520px]">
							<div className="flex-1 flex overflow-hidden max-h-full p-8 w-full text-left align-middle transition-all transform lg:rounded-2xl bg-white dark:bg-neutral-900 dark:border dark:border-slate-700 dark:text-slate-100 shadow-xl">
								<span className="absolute right-3 top-3 z-50">
									<ButtonClose onClick={onCloseModal} />
								</span>

								<div className="flex-1 overflow-y-auto rounded-xl hiddenScrollbar">
									<h3 className="text-xl font-semibold mb-4">
										Congés / Récup.
										<span className="block text-sm font-normal text-slate-500 mt-1">
											{employees.length} employé{employees.length > 1 ? "s" : ""}{" "}
											sélectionné{employees.length > 1 ? "s" : ""}
										</span>
									</h3>

									<ul className="mb-4 flex flex-wrap gap-2">
										{employees.map((emp) => (
											<li
												key={emp._id}
												className="text-xs bg-slate-100 dark:bg-slate-700 rounded px-2 py-1"
											>
												{emp.firstName} {emp.lastName}
											</li>
										))}
									</ul>

									<div className="flex flex-row items-center gap-3 mb-2">
										<input
											placeholder="Date début"
											className="rounded text-sm"
											type="date"
											value={dateFrom}
											onChange={({ target }) => setDateFrom(target.value)}
										/>
										<input
											placeholder="Date fin"
											className="rounded text-sm"
											type="date"
											value={dateTo}
											onChange={({ target }) => setDateTo(target.value)}
										/>
									</div>
									<div className="flex flex-row items-center gap-3 mb-2">
										<select
											className="rounded text-sm"
											value={type}
											onChange={({ target }) => setType(target.value)}
										>
											<option value="">— Type —</option>
											<option value="CONGES">Congés à déduire</option>
											<option value="RECUP">Récup. à ajouter</option>
											<option value="RECUP_MOINS">Récup. à déduire</option>
										</select>
										<input
											min={0}
											max={type === "CONGES" ? 18 : 2}
											placeholder="Quantité"
											type="number"
											step="0.5"
											className="rounded text-sm text-right w-[80px]"
											value={qty}
											onChange={({ target }) =>
												setQty(Math.abs(parseFloat(target.value)))
											}
										/>
									</div>
									<div className="flex flex-row items-center gap-2 mt-1">
										<input
											className="rounded text-sm flex-1"
											placeholder="Commentaire"
											type="text"
											value={comment}
											onChange={({ target }) => setComment(target.value)}
										/>
										<ButtonPrimary
											className="!shadow-none"
											onClick={onSave}
											disabled={!type || !dateFrom || !dateTo || qty == 0 || saving}
										>
											{saving ? (
												<span className="text-sm">...</span>
											) : (
												<CheckIcon className="h-6 w-6" />
											)}
										</ButtonPrimary>
									</div>
								</div>
							</div>
						</div>
					</TransitionChild>
				</div>
			</Dialog>
		</Transition>
	);
};

export default ModalCongesRecupMulti;
